import React, { useCallback, useEffect, useRef, useState } from 'react';
import { regionService } from '../../services';
import { useUser } from '../../context/UserContext';
import { RegionMapPicker } from '../../components';
import { AdminPageHeader, Btn, Card, CheckBox, DataTable, Field, SearchField, inputClass } from '../../components/admin/shell';

const PAGE = 100;
const EMPTY_FORM = {
  name: '', slug: '', country: 'CA',
  northLat: '', southLat: '', eastLng: '', westLng: '',
  centerLat: '', centerLng: '', isActive: true,
};
const BOX_KEYS = ['northLat', 'southLat', 'eastLng', 'westLng', 'centerLat', 'centerLng'];

const slugify = (s) =>
  (s || '').toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const isBlank = (v) => v === '' || v === null || v === undefined;

/**
 * SYSTEM › REGIONS — spec/sections.md §24. ~69k areas, almost all auto-created from event
 * locations, so the list is searched and paged rather than listed. `+ ADD A REGION`
 * (`regions` · create) opens a card between the search and the table; validation is
 * inline under each field. No row editor exists yet — `regions` · edit is reserved.
 */
export default function RegionsPage() {
  const { can } = useUser();
  const canCreate = can('regions', 'create');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const fetchRegions = useCallback(async (append = false) => {
    setLoading(true);
    const skip = append ? rowsRef.current.length : 0;
    const r = await regionService.search({ q: query, skip, take: PAGE });
    if (r.success) {
      const page = r.data || [];
      setRows((cur) => (append ? [...cur, ...page] : page));
      // A short page means we've reached the end — no separate count call needed.
      setHasMore(page.length === PAGE);
    }
    setLoading(false);
  }, [query]);

  // Refetch from the top whenever the search term settles.
  useEffect(() => {
    const t = setTimeout(() => fetchRegions(false), 250);
    return () => clearTimeout(t);
  }, [fetchRegions]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] || e.server ? { ...e, [key]: undefined, server: undefined } : e));
  };

  const handleNameBlur = () => {
    if (form.name && !form.slug) setForm((f) => ({ ...f, slug: slugify(f.name) }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'A NAME IS REQUIRED.';
    if (!form.slug.trim()) next.slug = 'A SLUG IS REQUIRED.';
    else if (!/^[a-z0-9-]+$/.test(form.slug.trim())) next.slug = 'LOWERCASE LETTERS, DIGITS AND HYPHENS ONLY.';
    if (!/^[A-Za-z]{2}$/.test(form.country || '')) next.country = 'TWO LETTERS, LIKE CA.';
    if (BOX_KEYS.some((k) => isBlank(form[k]))) next.box = 'PICK A CITY OR SET A RADIUS — THE BOX AND CENTRE ARE REQUIRED.';
    return next;
  };

  const save = async () => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setSaving(true);
    const payload = {
      ...form,
      name: form.name.trim(),
      slug: form.slug.trim(),
      country: form.country.toUpperCase(),
      northLat: parseFloat(form.northLat),
      southLat: parseFloat(form.southLat),
      eastLng: parseFloat(form.eastLng),
      westLng: parseFloat(form.westLng),
      centerLat: parseFloat(form.centerLat),
      centerLng: parseFloat(form.centerLng),
    };
    const r = await regionService.create(payload);
    setSaving(false);
    if (!r.success) {
      setErrors({ server: `COULD NOT CREATE THE REGION.${r.error ? ` — ${r.error}` : ''}` });
      return;
    }
    setForm(EMPTY_FORM);
    setErrors({});
    setAdding(false);
    fetchRegions(false);
  };

  const cancel = () => { setAdding(false); setForm(EMPTY_FORM); setErrors({}); };

  return (
    <>
      <AdminPageHeader
        section="regions"
        meta="Areas, almost all auto-created. Search, or add one by hand."
        actions={canCreate && !adding ? <Btn variant="white" onClick={() => setAdding(true)}>+ ADD A REGION</Btn> : null}
      />
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <SearchField value={query} onChange={setQuery} placeholder="SEARCH REGIONS" />
        </div>

        {canCreate && adding && (
          <Card title="ADD A REGION" right={<Btn size="card" onClick={cancel}>CANCEL</Btn>} bodyClass="flex flex-col gap-4 p-[14px]">
            <div className="flex flex-col gap-[6px]">
              <RegionMapPicker
                value={form}
                onChange={(next) => {
                  setForm((f) => ({
                    ...f,
                    ...next,
                    // Keep existing manual edits to slug + isActive untouched.
                    slug: f.slug || (next.name ? slugify(next.name) : ''),
                    // Country falls back to existing if geocoder didn't return one.
                    country: next.country || f.country,
                  }));
                  setErrors((e) => (e.box || e.server ? { ...e, box: undefined, server: undefined } : e));
                }}
              />
              {errors.box && <div className="font-mono text-[11px] text-[#DC2626]">{errors.box}</div>}
            </div>

            <div className="grid grid-cols-1 gap-3 border-t border-[#F2F2F2] pt-4 md:grid-cols-3">
              <Field label="DISPLAY NAME" error={errors.name}>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setField('name', e.target.value)}
                  onBlur={handleNameBlur}
                  placeholder="Vancouver, BC"
                  className={inputClass}
                />
              </Field>
              <Field label="SLUG" error={errors.slug}>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setField('slug', e.target.value)}
                  placeholder="vancouver-bc"
                  className={inputClass}
                />
              </Field>
              <Field label="COUNTRY (ISO 2)" error={errors.country}>
                <input
                  type="text"
                  maxLength={2}
                  value={form.country}
                  onChange={(e) => setField('country', e.target.value.toUpperCase())}
                  placeholder="CA"
                  className={inputClass}
                />
              </Field>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <CheckBox checked={form.isActive} onChange={(v) => setField('isActive', v)} label="ACTIVE" />
              <Btn variant="solid" onClick={save} disabled={saving}>{saving ? 'SAVING…' : 'CREATE REGION'}</Btn>
            </div>
            {errors.server && <div className="font-mono text-[11px] text-[#DC2626]">{errors.server}</div>}

            <p className="font-mono text-[10px] text-[#6B7280]">
              Workflow: type a city above → Mapbox auto-populates the bounding box and center. Tweak with the radius
              input if you want a tighter or wider net. The map shows what guests will land in for this region.
            </p>
          </Card>
        )}

        <DataTable
          columns={[
            { key: 'name', label: 'NAME', fr: 2 },
            { key: 'slug', label: 'SLUG', fr: 1.6 },
            { key: 'country', label: 'COUNTRY', fr: 0.7, render: (r) => r.country || '—' },
            { key: 'center', label: 'CENTER', fr: 1.4, render: (r) => (r.centerLat != null && r.centerLng != null ? `${Number(r.centerLat).toFixed(4)}, ${Number(r.centerLng).toFixed(4)}` : '—') },
            { key: 'isActive', label: 'ACTIVE', fr: 0.8, render: (r) => ((r.isActive ?? true) ? 'YES' : 'NO') },
          ]}
          rows={rows}
          loading={loading}
          empty={query.trim() ? 'NO REGIONS MATCH THAT SEARCH.' : 'NO REGIONS YET.'}
          footerLeft={`${rows.length.toLocaleString()}${hasMore ? '+' : ''} shown`}
          loadMore={hasMore && !loading ? () => fetchRegions(true) : undefined}
        />
      </div>
    </>
  );
}
